import { Injectable } from '@angular/core';

@Injectable()
export class WeeklyScheduleService {
  private static readonly SECONDS_IN_MINUTE = 60;
  private static readonly MINUTES_IN_HOUR = 60;
  private static readonly HOURS_IN_DAY = 24;

  private static readonly SECONDS_IN_HOUR =
    WeeklyScheduleService.MINUTES_IN_HOUR * WeeklyScheduleService.SECONDS_IN_MINUTE;

  private static readonly SECONDS_IN_DAY = WeeklyScheduleService.HOURS_IN_DAY * WeeklyScheduleService.SECONDS_IN_HOUR;

  private static readonly TIME_SEPARATOR = ':';
  private static readonly DEFAULT_PAD = '0';

  toTimeInputValue(time?: string | null, fallback = '09:00'): string {
    if (!time) {
      return fallback;
    }

    const match = time.match(/^(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : fallback;
  }

  durationToTimeInputValue(duration?: string | null, fallback = '17:00'): string {
    if (!duration) {
      return fallback;
    }

    const totalSeconds = this.parseDurationToSeconds(duration);
    const normalizedSeconds = this._normalizeSeconds(totalSeconds);
    const { hours, minutes } = this._getHoursAndMinutes(normalizedSeconds);
    return `${this._pad(hours)}:${this._pad(minutes)}`;
  }

  parseDurationToSeconds(duration: string): number {
    const [dayPart, timePart] = duration.includes('.') ? duration.split('.') : ['0', duration];
    const [hours = 0, minutes = 0, seconds = 0] = timePart.split(WeeklyScheduleService.TIME_SEPARATOR).map(Number);
    return (
      Number(dayPart) * WeeklyScheduleService.SECONDS_IN_DAY +
      hours * WeeklyScheduleService.SECONDS_IN_HOUR +
      minutes * WeeklyScheduleService.SECONDS_IN_MINUTE +
      seconds
    );
  }

  parseTimeToSeconds(time: string): number {
    const [hours = 0, minutes = 0, seconds = 0] = time.split(WeeklyScheduleService.TIME_SEPARATOR).map(Number);
    return hours * WeeklyScheduleService.SECONDS_IN_HOUR + minutes * WeeklyScheduleService.SECONDS_IN_MINUTE + seconds;
  }

  formatSecondsTo12Hour(totalSeconds: number): string {
    const normalizedSeconds = this._normalizeSeconds(totalSeconds);
    const { hours, minutes } = this._getHoursAndMinutes(normalizedSeconds);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHour}:${this._pad(minutes)} ${period}`;
  }

  private _normalizeSeconds(seconds: number): number {
    const day = WeeklyScheduleService.SECONDS_IN_DAY;
    return ((seconds % day) + day) % day;
  }

  private _pad(value: number): string {
    return value.toString().padStart(2, WeeklyScheduleService.DEFAULT_PAD);
  }

  private _getHoursAndMinutes(seconds: number): { hours: number; minutes: number } {
    const hours = Math.floor(seconds / WeeklyScheduleService.SECONDS_IN_HOUR);
    const minutes = Math.floor(
      (seconds % WeeklyScheduleService.SECONDS_IN_HOUR) / WeeklyScheduleService.SECONDS_IN_MINUTE,
    );

    return { hours, minutes };
  }
}
